// Pure highlight rules over the sheet games: longest run, biggest lead and
// biggest comeback. Only rally columns count; markers and conduct are skipped.
import type { SheetColumn, SheetGame, SheetSide } from './scoreSheet';

export interface MatchHighlights {
  run: { length: number; side: SheetSide; game: number } | null;
  lead: { points: number; side: SheetSide; game: number; score: [number, number] } | null;
  comeback: {
    from: [number, number];
    to: [number, number];
    side: SheetSide;
    game: number;
  } | null;
}

/** A recovered deficit smaller than this is not worth a highlight. */
const MIN_COMEBACK_DEFICIT = 3;

type Rally = Extract<SheetColumn, { kind: 'rally' }>;
const rallies = (game: SheetGame): Rally[] =>
  game.columns.filter((c): c is Rally => c.kind === 'rally');

/** Score difference (side 0 minus side 1) after each rally of the game. */
export function leadPath(game: SheetGame): number[] {
  return rallies(game).map((r) => r.score[0] - r.score[1]);
}

export function matchHighlights(games: SheetGame[]): MatchHighlights {
  let run: MatchHighlights['run'] = null;
  let lead: MatchHighlights['lead'] = null;
  let comeback: (NonNullable<MatchHighlights['comeback']> & { deficit: number }) | null = null;

  for (const game of games) {
    let current: SheetSide | null = null;
    let length = 0;
    // Per side: the worst deficit so far in this game and the score it was at.
    const worst: Array<{ deficit: number; score: [number, number] }> = [
      { deficit: 0, score: [0, 0] },
      { deficit: 0, score: [0, 0] },
    ];

    for (const r of rallies(game)) {
      length = r.scorer === current ? length + 1 : 1;
      current = r.scorer;
      if (!run || length > run.length) run = { length, side: r.scorer, game: game.number };

      const diff = Math.abs(r.score[0] - r.score[1]);
      if (diff > (lead?.points ?? 0)) {
        lead = {
          points: diff,
          side: r.score[0] > r.score[1] ? 0 : 1,
          game: game.number,
          score: r.score,
        };
      }

      for (const side of [0, 1] as const) {
        const mine = r.score[side];
        const theirs = r.score[1 - side];
        if (theirs - mine > worst[side].deficit) {
          worst[side] = { deficit: theirs - mine, score: r.score };
        }
        if (mine > theirs && worst[side].deficit > (comeback?.deficit ?? 0)) {
          comeback = {
            deficit: worst[side].deficit,
            from: worst[side].score,
            to: r.score,
            side,
            game: game.number,
          };
        }
      }
    }
  }

  return {
    run,
    lead,
    comeback:
      comeback && comeback.deficit >= MIN_COMEBACK_DEFICIT
        ? { from: comeback.from, to: comeback.to, side: comeback.side, game: comeback.game }
        : null,
  };
}
